import React from "react";

/**
 * The last boundary. `ChunkErrorBoundary` sits inside the router and only covers a route chunk
 * that fails to load; a throw during render anywhere else — a provider, the navigation, a grid —
 * had nothing above it and unmounted the whole tree to a blank page with no way back.
 *
 * Deliberately plain: no styled-components, no theme, no i18n. Any of those can be what threw,
 * and a fallback that needs the machinery it is apologising for renders nothing at all. Inline
 * styles and hardcoded English are the point.
 */
type Props = { children: React.ReactNode };
type State = { error: Error | null };

const containerStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 16,
  minHeight: "100vh",
  padding: 24,
  fontFamily: "system-ui, sans-serif",
  color: "#1f2933",
  background: "#f5f7fa",
  textAlign: "center",
};

const buttonStyle: React.CSSProperties = {
  padding: "8px 20px",
  border: "1px solid #52606d",
  borderRadius: 4,
  background: "transparent",
  color: "inherit",
  font: "inherit",
  cursor: "pointer",
};

const detailStyle: React.CSSProperties = {
  maxWidth: 720,
  overflowX: "auto",
  padding: 12,
  borderRadius: 4,
  background: "#e4e7eb",
  fontSize: 12,
  textAlign: "left",
  whiteSpace: "pre-wrap",
};

class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // The console is the only sink this app has today. When an error reporter is added, this is
    // the one place that needs to call it.
    console.error("Unhandled render error:", error, errorInfo.componentStack);
  }

  render() {
    const { error } = this.state;

    if (!error) return this.props.children;

    return (
      <div style={containerStyle} role="alert">
        <h1 style={{ margin: 0, fontSize: 20 }}>Something went wrong</h1>
        <p style={{ margin: 0, color: "#52606d" }}>
          The page could not be displayed. Reloading usually clears it.
        </p>
        <button type="button" style={buttonStyle} onClick={() => window.location.reload()}>
          Reload
        </button>

        {/* The stack is a developer's tool, and in production it is also a detail about the
            app's internals that a back office user has no use for. */}
        {process.env.NODE_ENV !== "production" && (
          <pre style={detailStyle}>{error.stack || error.message}</pre>
        )}
      </div>
    );
  }
}

export default AppErrorBoundary;
